import { Capacitor, CapacitorHttp } from '@capacitor/core'
import { AUTH_HEADER_SCHEME } from '@/shared/constants/auth'
import {
  APP_API_PROXY,
  KANGURO_API_PROXY,
  KANGURO_INTEGRATED_API_PROXY,
} from '@/shared/constants/api'

const isNative = Capacitor.isNativePlatform()
const APP_BASE_URL = process.env.BUN_PUBLIC_APP_BASE_URL ?? ''

export type ApiVersion = 'v1' | 'v2' | 'app'

type ApiTarget = {
  proxy: string
  unwrap: boolean
}

const API_TARGETS: Record<ApiVersion, ApiTarget> = {
  v1: { proxy: KANGURO_API_PROXY, unwrap: false },
  v2: { proxy: KANGURO_INTEGRATED_API_PROXY, unwrap: false },
  app: { proxy: APP_API_PROXY, unwrap: true },
}

function unwrapData<T>(target: ApiTarget, payload: T): T {
  if (!target.unwrap) return payload
  return (payload as { data?: T } | undefined)?.data as T
}

export async function apiRequest<T>(
  path: string,
  token: string,
  method: string = 'GET',
  body?: object | object[],
  version: ApiVersion = 'v1',
): Promise<T> {
  const target = API_TARGETS[version]
  const url = `${isNative ? APP_BASE_URL : ''}${target.proxy}${path}`
  const headers: Record<string, string> = {
    Authorization: `${AUTH_HEADER_SCHEME} ${token}`,
    ...(body ? { 'Content-Type': 'application/json' } : {}),
  }

  if (isNative) {
    const response = await CapacitorHttp.request({
      url,
      method,
      headers,
      ...(body ? { data: body } : {}),
    })
    if (response.status >= 400) {
      throw new Error(`Request failed: ${response.status}`)
    }
    return unwrapData(target, response.data as T)
  }

  const response = await fetch(url, {
    method,
    headers,
    ...(body ? { body: JSON.stringify(body) } : {}),
  })
  if (!response.ok) {
    throw new Error(`Request failed: ${response.status}`)
  }
  if (response.status === 204) return undefined as T
  try {
    return unwrapData(target, (await response.json()) as T)
  } catch {
    return undefined as T
  }
}
